import { RadioButtonWithLabelGroupWidget, RadioButtonWithLabelOption } from '@tableau/widgets';
import { useFormikContext } from 'formik';
import * as React from 'react';
import { ServiceLocator } from '../../../../../app/ServiceLocator';
import { Messages } from '../../../../../codegen/Localize';
import { NumberInputRange } from '../../../../../ts/directives/tbRangeInputValidator';
import { FeatureFlags } from '../../../../../ts/FeatureFlags';
import { ILicensingInfoWithTotals, LicensesService } from '../../../../../ts/services/LicensesService';
import { disabledText, settingsRadioButtonItemHeight } from '../../../../styles/CommonStyles';
import { useSharedWidgetChangeHandler } from '../../../utils';
import { ManageUsersSectionProps } from './ManageUsersSection';
import { ManageUsersServerLimitOption } from './ManageUsersServerLimitOption';
import { ManageUsersSiteRoleQuotaOption } from './ManageUsersSiteRoleQuotaOption';
import { ManageUsersSiteUserQuotaOption } from './ManageUsersSiteUserQuotaOption';

export const USER_QUOTA_MODE_FIELD = 'settings.userQuotaMode';
export function ManageUsersAdminOption(props: ManageUsersSectionProps) {
  const formik = useFormikContext<ManageUsersSectionProps>();
  const disabled = ! formik.values.settings.siteAdminsCanAddUsers;

  const licenseMaxCounts = useLicensingInfoInfo();

  const buttons = React.useMemo(() => getButtonsList({
    ...props,
    maxCounts: licenseMaxCounts
  }, disabled), [props, licenseMaxCounts, disabled]);

  const index = buttons.findIndex(button => button.value === formik.values.settings.userQuotaMode);

  const handleChange = useSharedWidgetChangeHandler<string>(USER_QUOTA_MODE_FIELD);

  return <>
    <div>{Messages.serverAndSiteAdmins()}</div>
    <div className={props.settings.siteAdminsCanAddUsers ? undefined : disabledText}>{Messages.usersDetails()}</div>
    <RadioButtonWithLabelGroupWidget buttonList={buttons}
                                     aria-label={Messages.managingUsersDetails()}
                                     className={settingsRadioButtonItemHeight}
                                     checkedIndex={index}
                                     handleChange={handleChange}/>
  </>;
}

function getButtonsList(props: ManageUsersSectionProps & { maxCounts: LicenseMaxCounts }, disabled: boolean): RadioButtonWithLabelOption<string>[] {
  const buttons: RadioButtonWithLabelOption<string>[] = [
    {
      value: 'server-limit',
      label: <ManageUsersServerLimitOption {...props}/>,
      testId: 'user-quota-unlimited-radio-button',
      disabled
    },
    {
      value: 'site-user-quota',
      label: <ManageUsersSiteUserQuotaOption {...props}/>,
      testId: 'user-quota-enabled-radio-button',
      disabled
    }
  ];

  if(FeatureFlags.siteQuotaEnabled) {
    buttons.push({
      value: 'site-tiered-capacity',
      label: <ManageUsersSiteRoleQuotaOption {...props}/>,
      testId: 'site-tiered-quota-enabled-radio-button',
      disabled
    });
  }

  return buttons;
}

export interface LicenseMaxCounts {
  userCountMax: number;
  tierCreatorCountMax: number;
  tierExplorerCountMax: number;
  tierViewerCountMax: number;
}

export function useLicensingInfoInfo() {
  const [licenseMaxCounts, setLicenseMaxCounts] = React.useState<LicenseMaxCounts>(null);

  React.useEffect(() => {
    if (!licenseMaxCounts) {
      ServiceLocator.get<LicensesService>('LicensesService').fetch().then((licensingInfo: ILicensingInfoWithTotals) => {
        if (licensingInfo) {
          const counts = calculateUserMaxCounts(licensingInfo);
          setLicenseMaxCounts(counts);
        }
      });
    }
  }, [licenseMaxCounts]);

  return licenseMaxCounts;
}

export function calculateUserMaxCounts(licensingInfo: ILicensingInfoWithTotals): LicenseMaxCounts {
  let tierCreatorCountMax = licensingInfo.totalCreatorUsers ?? 0;
  let tierExplorerCountMax = licensingInfo.totalExplorerUsers ?? 0;
  let tierViewerCountMax = licensingInfo.totalViewerUsers ?? 0;

  const noLicenses = !licensingInfo.productKeyInfos || !licensingInfo.productKeyInfos.length;
  if (licensingInfo.coreLicensingInfos || noLicenses) {
    tierExplorerCountMax = NumberInputRange.INT.MAX;
    tierCreatorCountMax = 0;
    tierViewerCountMax = 0;
  }
  const userCountMax = tierCreatorCountMax + tierExplorerCountMax + tierViewerCountMax;

  return {
    userCountMax,
    tierCreatorCountMax,
    tierExplorerCountMax,
    tierViewerCountMax
  };
}
